import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Mail, Save, Send, Eye, EyeOff } from "lucide-react";

export default function AdminEmail() {
  const [form, setForm] = useState({ provider: "gmail", smtp_host: "smtp.gmail.com", smtp_port: 587, smtp_email: "", smtp_password: "", smtp_from_name: "CrystoBag", enabled: false });
  const [hasPassword, setHasPassword] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [loading, setLoading] = useState("");

  useEffect(() => {
    API.get("/admin/email-settings").then(r => {
      const d = r.data;
      setForm({ provider: d.provider || "gmail", smtp_host: d.smtp_host || "smtp.gmail.com", smtp_port: d.smtp_port || 587, smtp_email: d.smtp_email || "", smtp_password: "", smtp_from_name: d.smtp_from_name || "CrystoBag", enabled: d.enabled || false });
      setHasPassword(d.smtp_password_set || false);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setLoading("save");
    try {
      const body = { ...form };
      if (!body.smtp_password && hasPassword) delete body.smtp_password;
      await API.put("/admin/email-settings", body);
      toast.success("Email settings saved");
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const sendTest = async () => {
    setLoading("test");
    try {
      const { data } = await API.post("/admin/email-settings/test", { to: testEmail || form.smtp_email });
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  return (
    <AdminLayout title="Email Settings">
      <div className="space-y-6 max-w-3xl" data-testid="admin-email">
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#00F0FF]" />
            <div>
              <h3 className="text-lg font-bold text-[#F8FAFC]">Email Configuration</h3>
              <p className="text-xs text-[#64748B]">Used for registration verification, password reset, and notifications</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => setForm({ ...form, enabled: !form.enabled })}
              className={`px-4 py-2 text-sm font-bold rounded-lg border ${form.enabled ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]"}`}>
              Email: {form.enabled ? "ENABLED" : "DISABLED"}
            </button>
            <select value={form.provider} onChange={(e) => {
              const p = e.target.value;
              if (p === "gmail") setForm({ ...form, provider: p, smtp_host: "smtp.gmail.com", smtp_port: 587 });
              else if (p === "outlook") setForm({ ...form, provider: p, smtp_host: "smtp-mail.outlook.com", smtp_port: 587 });
              else setForm({ ...form, provider: p });
            }} className="h-10 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
              <option value="gmail">Gmail</option>
              <option value="outlook">Outlook</option>
              <option value="custom">Custom SMTP</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#94A3B8] text-sm">SMTP Host</Label>
              <Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">SMTP Port</Label>
              <Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value) })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Email Address</Label>
              <Input value={form.smtp_email} onChange={(e) => setForm({ ...form, smtp_email: e.target.value })} placeholder="your@gmail.com" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Password / App Password {hasPassword && "(set, leave blank to keep)"}</Label>
              <div className="flex gap-2 mt-1">
                <Input type={showPw ? "text" : "password"} value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                  placeholder={hasPassword ? "Leave blank to keep current" : "App password"} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] flex-1" />
                <button onClick={() => setShowPw(!showPw)} className="p-2 text-[#64748B] border border-[#2A3241] rounded-lg">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.provider === "gmail" && <p className="text-[9px] text-[#64748B] mt-1">For Gmail: Use an App Password (Google Account → Security → 2FA → App Passwords)</p>}
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">From Name</Label>
              <Input value={form.smtp_from_name} onChange={(e) => setForm({ ...form, smtp_from_name: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
          </div>

          <button onClick={save} disabled={loading === "save"} className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg disabled:opacity-50">
            <Save className="w-4 h-4" /> {loading === "save" ? "Saving..." : "Save Settings"}
          </button>
        </div>

        {/* Test */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-3">
          <h3 className="text-sm font-bold text-[#F8FAFC]">Send Test Email</h3>
          <div className="flex gap-2">
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="recipient@email.com (or leave blank for self)" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] flex-1" />
            <button onClick={sendTest} disabled={loading === "test"} className="flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase bg-[#FFD600] text-[#0B0E14] rounded-lg disabled:opacity-50">
              <Send className="w-4 h-4" /> {loading === "test" ? "Sending..." : "Send Test"}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
